import { CircularProgress, Typography } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { ApplicationAnswerProps } from '@/models/Application';
import { Currency } from '@/models/Currency';
import { ProgramNames } from '@/models/Program';
import { QuestionAlias, QuestionProps } from '@/models/Question';
import { QuestionChoiceProps } from '@/models/QuestionChoice';
import { StandardQuestionDetails } from '@/models/Standard';
import { logError } from '@/utils/errorHandlerUtil';

const TotalExpensesValue = ({ question }: any) => {
  const {
    canEdit,
    answers,
    questions,
    currentStandardId,
    currentApplication,
    makingApiRequest,
    updateMakingApiRequest,
    saveApplicationAnswer,
  } = useContext(CurrentApplicationContext);
  const { currencies, selectedProgramId } = useContext(SelectedProgramContext);
  const [totalExpenseValue, setTotalExpenseValue] = useState<number>(0);

  const questionAnswer = useMemo(() => {
    return answers[question.id];
  }, [answers, question.id]);

  const questionsInStandard = useMemo(
    () =>
      questions.standards.find((st: StandardQuestionDetails) => st.standardId == currentStandardId)
        ?.questions ?? [],
    [questions, currentStandardId]
  );

  const currency: Currency | null = useMemo(() => {
    const currencyQuestion = questionsInStandard.find(
      (q) => q.alias == QuestionAlias.TCOW_Expenditure_Currency
    );

    if (!currencyQuestion) return null;

    const currencyQuestionChoice = answers[currencyQuestion.id]?.choices[0];

    if (!currencyQuestion?.questionChoices || !currencyQuestionChoice) return null;

    const currencyChoice: string | undefined = currencyQuestion.questionChoices.find(
      (choice: QuestionChoiceProps) => choice.choiceId == currencyQuestionChoice.choiceId
    )?.choice;

    if (currencyChoice == null) return null;

    return currencies.find((curr) => currencyChoice.includes(curr.name)) ?? null;
  }, [answers, currencies, questionsInStandard]);

  const includedInTotalQuestionSum: number = useMemo(() => {
    if (answers == null || questionsInStandard == null) return 0;

    // find sum of all includedInTotal questions
    const includedInTotal = Object.values<ApplicationAnswerProps>(answers).filter(
      (answer: ApplicationAnswerProps) =>
        answer.includeInTotal != null &&
        questionsInStandard.some((q: QuestionProps) => q.id === answer.questionId)
    );

    const total = includedInTotal?.reduce(function (acc: number, ob: ApplicationAnswerProps) {
      return acc + (ob.responseInt ?? 0) + (ob.responseDecimal ?? 0);
    }, 0);

    return total ?? 0;
  }, [answers, questionsInStandard]);

  // this will no longer work if we introduce a second question type that is hours on the current standard
  const volunteerHoursQuestionAnswer = useMemo(
    () =>
      Object.values<ApplicationAnswerProps>(answers).find(
        (answer: ApplicationAnswerProps) =>
          answer.questionType == 'hours' &&
          questionsInStandard.some((qu: QuestionProps) => qu.id == answer.questionId)
      ),
    [answers, questionsInStandard]
  );

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (volunteerHoursQuestionAnswer && question.hourlyVolunteerRate == null) {
      logError(`Hourly volunteer rate is null for question ${question.id}`);
    }

    let totalExpenditures =
      includedInTotalQuestionSum +
      (volunteerHoursQuestionAnswer?.responseInt ?? 0) * (question.hourlyVolunteerRate ?? 1);

    totalExpenditures = Math.round((totalExpenditures + Number.EPSILON) * 100) / 100;

    setTotalExpenseValue(totalExpenditures);
  }, [
    volunteerHoursQuestionAnswer,
    includedInTotalQuestionSum,
    volunteerHoursQuestionAnswer?.responseInt,
    question.hourlyVolunteerRate,
    question.id,
    logError,
  ]);

  const [savingAnswer, setSavingAnswer] = useState(false);

  useEffect(() => {
    if (
      savingAnswer &&
      totalExpenseValue &&
      questionAnswer &&
      (totalExpenseValue == questionAnswer.responseInt ||
        totalExpenseValue == questionAnswer.responseDecimal)
    ) {
      setSavingAnswer(false);
    }
  }, [savingAnswer, totalExpenseValue, questionAnswer]);

  useEffect(() => {
    if (totalExpenseValue == 0 || !canEdit) {
      return;
    }

    if (
      totalExpenseValue &&
      questionAnswer &&
      totalExpenseValue != questionAnswer.responseInt &&
      totalExpenseValue != questionAnswer.responseDecimal &&
      currentApplication.id &&
      !savingAnswer
    ) {
      setSavingAnswer(true);
      saveApplicationAnswer(currentApplication.id, question.id, totalExpenseValue).then(() => {
        updateMakingApiRequest(false);
      });
    }
  }, [
    canEdit,
    currentApplication.id,
    question.id,
    questionAnswer,
    saveApplicationAnswer,
    savingAnswer,
    totalExpenseValue,
    updateMakingApiRequest,
  ]);

  useEffect(() => {
    setLoading(makingApiRequest);
  }, [makingApiRequest]);

  return (
    <div
      style={{
        marginBottom: [ProgramNames.TreeCityUSA, ProgramNames.TreeLineUSA].includes(
          selectedProgramId
        )
          ? '10px'
          : '20px',
      }}
    >
      <Typography variant='h3' style={{ marginTop: '20px', marginBottom: '10px' }}>
        {question.questionText}
      </Typography>
      <div>
        {totalExpenseValue && (
          <div
            style={{
              width: 'auto',
              display: 'inline-block',
            }}
          >
            {totalExpenseValue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
            {selectedProgramId === ProgramNames.TreeCitiesWorld && currency?.isoCode
              ? ` ${currency.isoCode}`
              : ' USD'}
          </div>
        )}
        {loading && (
          <div
            style={{
              display: 'inline-block',
              position: 'relative',
              top: '3px',
              width: '20px',
              height: '20px',
              marginLeft: '10px',
            }}
          >
            <CircularProgress size='16px' />
          </div>
        )}
      </div>
    </div>
  );
};

export default TotalExpensesValue;
